import type { StyleRule } from '@archipro-design/aria';
import {
    labelClassName,
    pxToRem,
    SubheaderClassName,
} from '@archipro-design/aria';
import type { BaseThemePrepared } from '@archipro-design/aria';
import { hex2rgba } from '~/utils/colorHelper';
import { svgIconClassName } from '@archipro-design/icons';

const borderItem = (theme: BaseThemePrepared) => ({
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: theme.siteVariables.colors.grey[125]!,
});

export const Grid: StyleRule = ({ theme }) => ({
    display: 'flex',
    gridTemplateColumns: `${pxToRem(436)} 1fr`,
    width: '100%',
    borderTopWidth: '2px',
    borderTopStyle: 'solid',
    borderTopColor: theme.siteVariables.colors.grey[125],
    '& > div': {
        minHeight: pxToRem(56),
        alignItems: 'center',
        ...borderItem(theme),
    },
});

export const ListItem: StyleRule = ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    ...borderItem(theme),
    color: theme.siteVariables.colorScheme.default!.foreground,
    fontSize: pxToRem(16),
    fontWeight: 500,
});

export const ListItemLabel: StyleRule = ({ theme }) => ({
    [`&.${SubheaderClassName}`]: {
        letterSpacing: theme.siteVariables.letterSpacingNormal,
        color: theme.siteVariables.colorScheme.default!.foreground,
        fontSize: pxToRem(16),
        display: 'flex',
    },
});

export const LinkListItem: StyleRule = ({ theme }) => ({
    ...ListItem({ theme }),
    display: 'grid',
    gridTemplateColumns: `${pxToRem(45)} auto`,
    '& :first-child': {
        '& svg': {
            width: pxToRem(18),
            height: pxToRem(18),
            fill: theme.siteVariables.colorScheme.default!.foreground,
        },
    },
    ':hover': {
        backgroundColor: hex2rgba(
            theme.siteVariables.colors.primitive.white,
            0.1
        ),
    },
});

const baseContent = (theme: BaseThemePrepared) => ({
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: theme.siteVariables.colorScheme.default!.foreground,
    fontSize: pxToRem(16),
});

export const ListItemContent: StyleRule = ({ theme }) => ({
    ...baseContent(theme),
    maxWidth: pxToRem(1250),
});

export const ProfessionalContent: StyleRule = ({ theme }) => ({
    ...baseContent(theme),
    maxWidth: pxToRem(1000),

    [`& a`]: {
        color: theme.siteVariables.colors.charcoal[250],
        textDecoration: 'underline',
        textUnderlineOffset: pxToRem(3),
        fontWeight: 500,
    },
});

export const SendEnquiryButton: StyleRule = ({ theme }) => ({
    height: '100%',
    whiteSpace: 'nowrap',
    padding: theme.siteVariables.spaces['space-24'], // !important

    [`& .${labelClassName}`]: {
        color: theme.siteVariables.colors.charcoal[250],
        lineHeight: `${pxToRem(24)} !important`,
        fontSize: `${pxToRem(24)} !important`,
        textTransform: 'uppercase !important',
        letterSpacing: `${theme.siteVariables.letterSpacingSmall} !important`,
    },
    [theme.screen.laptop]: {
        maxHeight: pxToRem(54),
    },
});

export const LikeButton: StyleRule = ({ theme }) => ({
    width: pxToRem(54),
    height: pxToRem(54),
    '&:hover': {
        background: 'transparent',
    },
    padding: 0,
    ['& .' + svgIconClassName]: {
        color: theme.siteVariables.colorScheme.default!.foreground,
        fill: theme.siteVariables.colorScheme.default!.foreground,
    },
});

export const LikeIcon: StyleRule = ({ theme }) => ({
    width: `${pxToRem(40)} !important`,
    height: `${pxToRem(40)} !important`,
    color: theme.siteVariables.colorScheme.default!.foreground,
    fill: 'currentColor',
    '& path': {
        fill: 'currentColor',
    },
});

export const ShowLessButton: StyleRule = ({ theme }) => ({
    marginTop: pxToRem(12),
    textAlign: 'left',
    textDecoration: 'underline',
    justifySelf: 'flex-start',

    '&:hover': {
        color: theme.siteVariables.colors.primitive.white,
        '& svg': {
            fill: theme.siteVariables.colors.primitive.white,
        },
    },
    [`& .${labelClassName}`]: {
        letterSpacing: `${theme.siteVariables.letterSpacingNormal} !important`,
    },
    '& svg': {
        width: pxToRem(29),
        height: pxToRem(30),
        marginTop: pxToRem(-7),
        marginLeft: pxToRem(10),
        fill: theme.siteVariables.colors.primitive.white,
    },
});

export const ShowMoreButton: StyleRule = ({ theme }) => ({
    ...ShowLessButton({ theme }),
    '&:hover': {
        color: theme.siteVariables.colors.primitive.white,
        '& svg path': {
            fill: theme.siteVariables.colors.primitive.white,
        },
    },
    '& svg': {
        width: pxToRem(19),
        height: pxToRem(19),
        marginTop: pxToRem(-1),
        marginLeft: pxToRem(15),
        '& path': {
            fill: theme.siteVariables.colors.primitive.white,
        },
    },
});

export const DetailsContainer: StyleRule = ({ theme }) => ({
    marginBottom: pxToRem(78),

    [theme.screen.laptop]: {
        marginTop: pxToRem(50),
    },
});
